import './InputNumber.scss';

import * as React from 'react';
import { useEffect, useMemo } from 'react';

import { IconButton } from '~shared/components/buttons';
import { useHold } from '~shared/hooks/useHold';

import { IconMinus, IconPlus } from '../icons/standard';

interface InputNumberProps {
  /** The id of the input element. */
  id: string;
  /** The label for the input element. */
  label: string;
  /** the name of the forum field **/
  name: string;
  /** The value of the input element. */
  value: number;
  /** The minimum value allowed for the input element. */
  min?: number;
  /** The maximum value allowed for the input element. */
  max?: number;
  /** The step value for incrementing/decrementing the input element. */
  step?: number;
  /** The delay in milliseconds for the increment/decrement hold action. */
  incrementDelay?: number;
  /** Callback function to handle value changes. */
  onChange: (value: number) => void;
}

interface CreateInputNumberReducerParams {
  min?: number;
  max?: number;
  step?: number;
}

const createInputNumberReducer =
  ({ min, max, step = 1 }: CreateInputNumberReducerParams) =>
  (
    state: number,
    action:
      | {
          type: 'increment';
        }
      | {
          type: 'decrement';
        }
      | {
          type: 'set';
          payload: { value: number };
        }
  ) => {
    const clamp = makeClamp({ min, max });
    switch (action.type) {
      case 'increment': {
        return clamp(state + step);
      }
      case 'decrement': {
        return clamp(state - step);
      }
      case 'set': {
        return clamp(action.payload.value);
      }
      default:
        return state;
    }
  };

export function InputNumber({
  id,
  label,
  value,
  onChange,
  name,
  max,
  min = 0,
  step = 1,
  incrementDelay = 200,
}: InputNumberProps) {
  const [innerValue, dispatch] = React.useReducer(
    createInputNumberReducer({ min, max, step }),
    value
  );

  const clamp = useMemo(() => makeClamp({ min, max }), [min, max]);

  useEffect(() => {
    dispatch({ type: 'set', payload: { value: value } });
  }, [clamp, value]);

  useEffect(() => {
    onChange(clamp(innerValue));
  }, [clamp, onChange, innerValue]);

  const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      dispatch({
        type: 'decrement',
      });
    } else if (event.key === 'ArrowUp') {
      dispatch({
        type: 'increment',
      });
    }
  };

  const holdDecrement = useHold({
    onHold: () =>
      dispatch({
        type: 'decrement',
      }),
    ms: incrementDelay,
  });
  const holdIncrement = useHold({
    onHold: () => dispatch({ type: 'increment' }),
    ms: incrementDelay,
  });

  return (
    <div className="grid items-center justify-between gap-1 border-solid border-2 border-grey-200 rounded-2 input-number">
      <label
        className="text-base text-sans text-grey-900 leading-12 pl-4"
        htmlFor={id}
      >
        {label}
      </label>
      <input
        className="text-right border-none text-base text-sans text-blue-900 min-w-0 h-full p-1"
        id={id}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={innerValue}
        name={name}
        onKeyDown={onKeyPress}
        onChange={(event) => {
          const value = parseInt(event.target.value, 10);
          if (isNaN(value)) {
            return;
          }
          // set the value
          dispatch({ type: 'set', payload: { value: value } });
        }}
      />
      <div className="flex flex-row items-center gap-1">
        <IconButton
          disabled={min && value <= min}
          onMouseDown={holdDecrement.handleOnMouseDown}
          onMouseUp={holdDecrement.handleOnMouseUp}
          onMouseLeave={holdDecrement.handleOnMouseLeave}
        >
          <IconMinus />
        </IconButton>
        {/* vertical divider */}
        <div
          className="border-l border-r-0 border-solid border-grey-200"
          style={{ height: '1.5em' }}
        ></div>
        <IconButton
          disabled={max && value >= max}
          onMouseDown={holdIncrement.handleOnMouseDown}
          onMouseUp={holdIncrement.handleOnMouseUp}
          onMouseLeave={holdIncrement.handleOnMouseLeave}
        >
          <IconPlus />
        </IconButton>
      </div>
    </div>
  );
}

export const makeClamp =
  ({ min, max }: { min: number; max: number }) =>
  (value: number | string) => {
    const parsed = Number(value);
    if (isNaN(parsed)) {
      return min;
    }
    return Math.min(Math.max(parsed, min), max);
  };
